/**
 * Delete a forge (ADR-023 A8). Shared by the list and the forge page.
 *
 * Pessimistic: nothing is removed from view until the server confirms. On
 * success the forge's cached detail, turns and test builds are dropped (a
 * refetch would only answer 410) and every forge list is refetched.
 *
 * The server stops a running forge itself, so the caller does not stop first.
 * A delete that reports `storagePurged: false` still succeeded — the server
 * finishes the cleanup later — so it is not surfaced as a problem. Whether the
 * arche was unpublished IS surfaced: it changes what everyone else can open.
 */
import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { forgeQueryKeys } from '../api/queryKeys';

export interface ForgeDeletedOutcome {
  /** The arche was published, and deleting its forge took it out of the Store. */
  archeUnpublished: boolean;
}

/** The toast both pages show after a delete. */
export function forgeDeletedToast(outcome: ForgeDeletedOutcome): {
  title: string;
  description?: string;
} {
  return outcome.archeUnpublished
    ? {
        title: 'Forge deleted',
        description: 'Its arche was unpublished and is no longer in the Arche Store.',
      }
    : { title: 'Forge deleted' };
}

export interface UseDeleteForgeResult {
  /** Rejects with the server's error; the caller shows it where the user confirmed. */
  deleteForge: (forgeId: string) => Promise<ForgeDeletedOutcome>;
  /** The forge a delete is in flight for (drives per-card spinners). */
  deletingForgeId: string | null;
}

export function useDeleteForge(): UseDeleteForgeResult {
  const queryClient = useQueryClient();
  const [deletingForgeId, setDeletingForgeId] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (forgeId: string) => api.deleteForge(forgeId),
    onMutate: (forgeId) => setDeletingForgeId(forgeId),
    onSuccess: (_result, forgeId) => {
      // `detail` is the prefix of the forge's turns and test-builds keys too.
      // Only INACTIVE entries go: removing a query the forge page is still
      // observing makes it refetch at once and log a 410 for a forge that is
      // gone. The page navigates away, and its entries expire unobserved.
      queryClient.removeQueries({ queryKey: forgeQueryKeys.detail(forgeId), type: 'inactive' });
      void queryClient.invalidateQueries({ queryKey: forgeQueryKeys.lists() });
    },
    onSettled: () => setDeletingForgeId(null),
  });

  const deleteForge = useCallback(
    async (forgeId: string): Promise<ForgeDeletedOutcome> => {
      const result = await mutation.mutateAsync(forgeId);
      return { archeUnpublished: result.archeUnpublished === true };
    },
    [mutation],
  );

  return { deleteForge, deletingForgeId };
}
